import { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSnapshot } from 'valtio';

import { AutomaticCarousel } from '@/components/Cms/AutomaticCarousel';
import { SliderProductsTypeEnum } from '@/components/Cms/SliderProducts';
import { useIdle } from '@/hooks/useIdle';
import { useRouter } from '@/hooks/useRouter';
import { formatProductSlug } from '@/lib/util';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { recentlyViewedPresenceState } from '@/states/recentlyViewedPresenceState';
import { useRecentlyViewedProducts } from '@/views/WatchView/helper';

import { isRecentlyViewedAllowed } from './recentlyViewedRoutes';

import * as S from './GatedRecentlyViewed.styles';
import { defined } from './messages';

/** Spec caps desktop at six; storage holds ten. One cap for both breakpoints. */
export const RECENTLY_VIEWED_MAX_PRODUCTS = 6;

type GatedRecentlyViewedProps = {
  /** Overrides `IDLE_TIMEOUT_IN_MS` (default 0 = show as soon as other gates pass). */
  idleTimeout?: number;
  /** Contentful page type, needed to tell Editorial from Legal on the CMS catch-all. */
  pageType?: string | null;
};

/**
 * Global Recently Viewed section (BEC-24811).
 *
 * Renders the PDP's own AutomaticCarousel driven by props rather than a
 * Contentful entry, so the two cannot drift apart. Gates, cheapest first:
 * eligible route, no CMS carousel already present, idle gate (default immediate),
 * products in storage.
 *
 * No brand gate — Gallet does not ship from this codebase.
 */
export const GatedRecentlyViewed = ({
  idleTimeout,
  pageType,
}: GatedRecentlyViewedProps) => {
  const { asPath, pathname, query } = useRouter();

  const isAllowedHere = isRecentlyViewedAllowed(pathname, query, pageType);

  // A CMS carousel on this page wins; otherwise we show once the idle gate fires
  // (default 0 ms via `NEXT_PUBLIC_IDLE_TIMEOUT_IN_MS`, or set env for a delay).
  const hasCmsCarousel = useSnapshot(recentlyViewedPresenceState).count > 0;

  // Armed only where the section can appear; this mounts on every Default page.
  // Path without the query: PLP filters push facets there and must not re-hide it.
  const hasBeenIdle = useIdle(idleTimeout, isAllowedHere, asPath.split('?')[0]);

  if (!isAllowedHere || hasCmsCarousel || !hasBeenIdle) {
    return null;
  }

  return <RecentlyViewedCarousel />;
};

GatedRecentlyViewed.displayName = 'GatedRecentlyViewed';

/**
 * Split out so the localStorage read and its cross-tab listener only run where
 * the section can appear — not on checkout. Hooks cannot be skipped, so the
 * work moves behind the gate instead.
 */
const RecentlyViewedCarousel = () => {
  const { formatMessage } = useIntl();
  const draftMode = useContext(DraftModeContext);

  // null: read the list without recording a view.
  const skus = useRecentlyViewedProducts(null);

  // Storage is newest-first, so take the head.
  const slugs = useMemo(
    () => skus.slice(0, RECENTLY_VIEWED_MAX_PRODUCTS).map(formatProductSlug),
    [skus],
  );

  if (!slugs.length) {
    return null;
  }

  return (
    <S.Wrapper>
      <AutomaticCarousel
        draftMode={draftMode}
        filter={{ slugs }}
        minProducts={1}
        registersPresence={false}
        showCta
        showPrice
        showWishlist
        // Show the whole stored list in one row; the CMS entry on the PDP sets its own.
        slidesPerView={RECENTLY_VIEWED_MAX_PRODUCTS}
        title={formatMessage(defined.title)}
        type={SliderProductsTypeEnum.RecentlyViewed}
      />
    </S.Wrapper>
  );
};

RecentlyViewedCarousel.displayName = 'RecentlyViewedCarousel';
